import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  TrendingUp, 
  Menu, 
  X, 
  ChevronDown,
  BarChart3,
  Globe,
  Star,
  BookOpen,
  FileText
} from 'lucide-react';

const Header = ({ user, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/screener', label: 'Screener', icon: BarChart3 },
    { path: '/markets', label: 'Marchés', icon: Globe },
    // WATCHLIST HIDDEN FOR LAUNCH - add back when feature is ready
    // { path: '/watchlist', label: 'Watchlist', icon: Star },
    { path: '/learn/valuation-guide', label: 'Guides', icon: BookOpen },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white hidden sm:block">
              VALOR<span className="text-emerald-400">AMAP</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {/* Public links - always visible */}
            <Link
              to="/screener"
              className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/screener')
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span>Screener</span>
            </Link>
            <Link
              to="/markets"
              className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/markets')
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span>Marchés</span>
            </Link>
            <Link
              to="/reports"
              className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/reports')
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Rapports d'Analyse</span>
            </Link>
            {/* WATCHLIST HIDDEN FOR LAUNCH - Uncomment when ready
            {user && (
              <Link
                to="/watchlist"
                className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/watchlist')
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <Star className="w-4 h-4" />
                <span>Watchlist</span>
              </Link>
            )}
            */}
            <Link
              to="/learn/valuation-guide"
              className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/learn/valuation-guide')
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              <span>Guides</span>
            </Link>
            {!user && (
              <Link
                to="/pricing"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/pricing')
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                Tarifs
              </Link>
            )}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {user ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-8 h-8 rounded-full border border-emerald-500/30"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-medium">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <span className="text-sm text-gray-300">{user.name}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onLogout}
                  className="text-gray-400 hover:text-white"
                >
                  Déconnexion
                </Button>
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                    Connexion
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    Essai 9 CHF
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col space-y-1">
              {/* Public links - always visible */}
              <Link
                to="/screener"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive('/screener')
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span>Screener</span>
              </Link>
              <Link
                to="/markets"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive('/markets')
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <Globe className="w-5 h-5" />
                <span>Marchés</span>
              </Link>
              <Link
                to="/reports"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive('/reports')
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <FileText className="w-5 h-5" />
                <span>Rapports d'Analyse</span>
              </Link>
              {/* WATCHLIST HIDDEN FOR LAUNCH - Uncomment when ready
              {user && (
                <Link
                  to="/watchlist"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive('/watchlist')
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Star className="w-5 h-5" />
                  <span>Watchlist</span>
                </Link>
              )}
              */}
              <Link
                to="/learn/valuation-guide"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive('/learn/valuation-guide')
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <BookOpen className="w-5 h-5" />
                <span>Guides</span>
              </Link>
              {!user && (
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5"
                >
                  Tarifs
                </Link>
              )}
              <div className="pt-4 mt-4 border-t border-white/10">
                {user ? (
                  <Button 
                    variant="ghost" 
                    onClick={onLogout}
                    className="w-full text-gray-400 hover:text-white justify-start"
                  >
                    Déconnexion
                  </Button>
                ) : (
                  <div className="flex flex-col space-y-2">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full text-gray-300">
                        Connexion
                      </Button>
                    </Link>
                    <Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                        Essai 9 CHF - 7 jours
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
